import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import { ThemeProvider } from "./lib/theme";
import { I18nProvider } from "./lib/i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import AILegalAssistant from "./pages/AILegalAssistant";
import Practice from "./pages/Practice";
import Team from "./pages/Team";
import Insights from "./pages/Insights";
import InsightDetail from "./pages/InsightDetail";
import Contact from "./pages/Contact";
import Contracts from "./pages/Contracts";
import Consultant from "./pages/Consultant";
import AdminStaff from "./pages/AdminStaff";
import AdminTraining from "./pages/AdminTraining";
import AdminLaws from "./pages/AdminLaws";
import AdminResearch from "./pages/AdminResearch";
import AdminJudgments from "./pages/AdminJudgments";
import JudgmentDetail from "./pages/JudgmentDetail";
import UnifiedSearch from "./pages/UnifiedSearch";
import Saved from "./pages/Saved";
import AdminDashboard from "./pages/AdminDashboard";
import AdminBookings from "./pages/AdminBookings";
import AdminContacts from "./pages/AdminContacts";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminSocialTopics from "./pages/AdminSocialTopics";
import AdminBlogDrafts from "./pages/AdminBlogDrafts";
import AdminLawQueue from "./pages/AdminLawQueue";
import AdminAuditLog from "./pages/AdminAuditLog";
import AdminTools from "./pages/AdminTools";
import Laws from "./pages/Laws";
import LawDetail from "./pages/LawDetail";
import LegalPage from "./pages/LegalPage";
import Subscriptions from "./pages/Subscriptions";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Research from "./pages/Research";
import Doctrine from "./pages/Doctrine";
import Judgments from "./pages/Judgments";
import StaffPrinciples from "./pages/StaffPrinciples";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Portal from "./pages/Portal";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import AdminLayout from "./components/AdminLayout";
import VerifyEmail from "./pages/VerifyEmail";
import ResendVerification from "./pages/ResendVerification";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import DocumentReview from "./pages/DocumentReview";
import CaseAssessment from "./pages/CaseAssessment";
import Terms from "./pages/Terms";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
};

function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PWAInstallPrompt />
            <Routes>
            <Route element={<Layout />}>
              {/* New premium pages */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/ai-legal-assistant" element={<AILegalAssistant />} />
              {/* Consultation booking removed (Feb 2026) — bookings now via WhatsApp only.
                  Old URLs redirect home so existing links/search-engine hits don't 404. */}
              <Route path="/consultation-booking" element={<Navigate to="/" replace />} />
              <Route path="/booking" element={<Navigate to="/" replace />} />
              <Route path="/booking/success" element={<Navigate to="/" replace />} />
              <Route path="/booking/error" element={<Navigate to="/" replace />} />
              <Route path="/practice" element={<Practice />} />
              <Route path="/team" element={<Team />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightDetail />} />
              <Route path="/contact" element={<Contact />} />
              {/* Existing AI / member pages */}
              <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
              <Route path="/consultant" element={<Protected><Consultant /></Protected>} />
              <Route path="/document-review" element={<Protected><DocumentReview /></Protected>} />
              <Route path="/case-assessment" element={<Protected><CaseAssessment /></Protected>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Terms />} />
              <Route path="/laws" element={<Laws />} />
              <Route path="/laws/:id" element={<LawDetail />} />
              <Route path="/refund-policy" element={<LegalPage />} />
              <Route path="/ai-disclaimer" element={<LegalPage />} />
              <Route path="/confidentiality-policy" element={<LegalPage />} />
              <Route path="/payment-policy" element={<LegalPage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/research" element={<Research />} />
              <Route path="/doctrine" element={<Doctrine />} />
              <Route path="/judgments" element={<Judgments />} />
              <Route path="/search" element={<Protected><UnifiedSearch /></Protected>} />
              <Route path="/saved" element={<Saved />} />
              <Route path="/judgments/:id" element={<JudgmentDetail />} />
              <Route
                path="/staff/principles"
                element={<Protected><StaffPrinciples /></Protected>}
              />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="social-topics" element={<AdminSocialTopics />} />
                <Route path="blog-drafts" element={<AdminBlogDrafts />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="training" element={<AdminTraining />} />
                <Route path="laws" element={<AdminLaws />} />
                <Route path="law-queue" element={<AdminLawQueue />} />
                <Route path="research" element={<AdminResearch />} />
                <Route path="judgments" element={<AdminJudgments />} />
                <Route path="principles" element={<StaffPrinciples />} />
                <Route path="tools" element={<AdminTools />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/portal" element={<Protected><Portal /></Protected>} />
              {/* Catch-all: send unknown URLs to home so deep-link typos don't 404 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
